import _, { camelCase, upperFirst } from 'lodash';
import {
  AdType,
  CreativeDeliverType,
  RtbCampaignPlanType,
  VideoAdViewObjective,
  DeliverType,
  CampaignState,
  OutdoorDeliverType,
  RtbCampaignFormData,
  SpaceDeliverabilityData
} from 'core/rtbCampaign/RtbCampaign';
import { getDayOfWeekLabelByValue } from 'components/Dayparts/Dayparts';
import { RtbCampaignSetupFlowPageModel } from '../RtbCampaignSetupFlowPageModel';
import { Currency } from 'core';
import { toast } from 'react-toastify';
import {
  FireableUpdateEventListener,
  UpdateEventListener
} from 'utils/UpdateEventListener';
import {
  RtbCampaignManager,
  DefaultRtbCampaignManager,
  LegacyEstimateViewModelData
} from 'core/rtbCampaign/RtbCampaignManager';
import { Order } from 'core/order/Order';
import { CampaignSetupTab } from './RtbCampaignSetupStepModel';
import i18n from 'i18next';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import {
  CreateRtbCampaignSetupFlowStep,
  CreateRtbCampaignWithCreativeSetupFlowStep,
  EditRtbCampaignSetupFlowStep
} from '../RtbCampaignSetupFlowStep';
import { L1Object } from 'core/l1Object/L1Object';
import {
  formatPriceWithCurrency,
  getCurrencyLabel
} from 'helper/CurrencyHelper';
import { EstimatedAudience } from 'core/limitation/Limitation';
import moment from 'moment';
import { DraftManager, RtbCampaignDraftManager } from 'core/draft/DraftManager';
import { BidStrategy } from 'core/l2Object/L2Object';
import { PmpFormModel } from '../RtbCampaignForm/PmpFormModel';
import { getPmpSummaryData } from 'containers/PmpOrder/PmpSummaryModel';
import {
  CreativeSummaryStepModel,
  CreativeSummaryStepState
} from 'containers/Creatives/CreativeSetupFlow/FlowSteps/CreativeSummaryStepModel';
import {
  FlowSummarySectionProps,
  SummarySectionData
} from 'components/FlowSummarySection/FlowSummarySection';
import { SummaryTitleColor } from 'components/SummaryDetail/SummaryDetail';
import { renderOverBudgetWording } from 'containers/L2Objects/FormHintRenderFunctions';
import { CreateCreativeInL2ObjectSetupFlowPageModel } from 'containers/Creatives/CreativeSetupFlow/CreativeSetupFlowPageModel';
import { Pmp, PmpType } from 'core/pmp/Pmp';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { getDisplayPrice } from 'helper/CampaignPriceHelper';
import { isAdTypeSupportOutdoorTA } from 'utils/CampaignUtil';
import { AbstractOutdoorLimitationCampaignFormModel } from '../RtbCampaignForm/OutdoorLimitationCampaignFormModel';
import { isTestMode } from 'utils/LimitationUtil';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { AbstractDynamicMovableOutdoorFormModel } from '../RtbCampaignForm/DynamicMovableOutdoorFormModel';

export type DeliveryTargetModalType =
  | 'deliverable'
  | 'weatherNonDeliverable'
  | 'changeNonDeliverable'
  // ECOPD-5621: 門市超過鎖定上限（電子紙一台機台最多 10 檔活動）。
  | 'overBindingLimitNonDeliverable';

export type RtbCampaignSummaryStepState = {
  readonly loading: boolean;
  readonly showPublishBindingFailed: boolean;
  readonly showAddSaveTargetingModal: boolean;
  readonly legacyEstimateData?: LegacyEstimateViewModelData;
  readonly spaceDeliverabilityData?: SpaceDeliverabilityData;
  readonly creativeSummaryStepState?: CreativeSummaryStepState;
  readonly deliveryTargetModalType?: DeliveryTargetModalType;
};

export interface RtbCampaignSummaryStepModel {
  readonly flowModel: RtbCampaignSetupFlowPageModel;
  readonly event: UpdateEventListener<RtbCampaignSummaryStepModel>;
  readonly state: RtbCampaignSummaryStepState;
  readonly targetingValue: any;
  readonly estimatedAudience?: EstimatedAudience;
  readonly audienceLowestThreshold: number;
  readonly appliedSavedTAInfo?: SelectOptions;
  readonly savedTargetingList: SelectOptions[];
  readonly order: Order;
  readonly l1Object?: L1Object;
  readonly campaign: RtbCampaignFormData;
  readonly noLegacyEstimateDes: string;
  readonly showLegacyEstimation: boolean;
  readonly showSpaceDeliverability: boolean;
  readonly creativeSummaryStepModel?: CreativeSummaryStepModel;
  readonly subStepIndex: number;
  readonly deliveryUnit?: DeliveryUnit;
  updateState(loading: boolean): void;
  getAdTypeSummaryData: () => FlowSummarySectionProps;
  getSetUpSummaryData: () => FlowSummarySectionProps;
  getPmpSummaryData: () => FlowSummarySectionProps | undefined;
  getPmpErrors: () => { [key: string]: string | JSX.Element };
  getLimitationsSummaryData: () => FlowSummarySectionProps;
  getTrackingSummaryData: () => FlowSummarySectionProps | undefined;
  getCreativeBindingSummaryData: () => FlowSummarySectionProps | undefined;
  submit: () => void;
  goLast: () => void;
  goStep: (stepIndex: number, subStepIndex: number) => void;
  goSubStep: (subStepIndex: number) => void;
  getLegacyEstimateData: () => Promise<void>;
  getSpaceDeliverabilityData: () => Promise<void>;
  onShowAddSaveTargetingModal: (showModal: boolean) => void;
  redirectToLastPage: () => void;
  initCreativeSummaryModel: () => void;
  openDeliveryTargetModal: (type: DeliveryTargetModalType) => void;
  closeDeliveryTargetModal: (type: DeliveryTargetModalType) => void;
  unmount: (eventHandler?: number) => void;
}

export type RtbCampaignSummaryStepProps = {
  readonly model: RtbCampaignSummaryStepModel;
};

export class DefaultRtbCampaignSummaryStepModel implements RtbCampaignSummaryStepModel {
  event: FireableUpdateEventListener<RtbCampaignSummaryStepModel>;
  loading: boolean;
  showPublishBindingFailed = false;
  showAddSaveTargetingModal = false;
  stepChangeListener?: (stepIndex, subStepName?: string) => void;
  legacyEstimateData?: LegacyEstimateViewModelData;
  spaceDeliverabilityData?: SpaceDeliverabilityData;
  creativeSummaryStepState?: CreativeSummaryStepState;
  creativeSummaryDataHanlder?: number;
  deliveryTargetModalType?: DeliveryTargetModalType;
  channelGroupSetting: ChannelGroupSetting;

  constructor(
    public flowModel: RtbCampaignSetupFlowPageModel,
    public subStepIndex: number,
    public goLast: () => void,
    public goStep: (stepIndex: number, subStepIndex?: number) => void,
    public goSubStep: (subStepIndex: number) => void,
    public creativeSummaryStepModel?: CreativeSummaryStepModel,
    protected campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager()
  ) {
    this.event = new FireableUpdateEventListener<RtbCampaignSummaryStepModel>();
    this.loading = false;
    this.channelGroupSetting = ChannelGroupSetting.fromId(
      flowModel.l1Object.channelGroupId
    );
  }

  get deliveryUnit() {
    if (!this.channelGroupSetting.isOutdoorChannel()) {
      return undefined;
    }
    return this.channelGroupSetting.config.deliveryUnit;
  }

  get isSummaryFlow() {
    return this.flowModel.type === 'summary';
  }

  get currencyLabel() {
    return this.flowModel
      ? getCurrencyLabel(this.flowModel.order.currency)
      : Currency.NTD;
  }

  get state() {
    return {
      loading: this.loading,
      showPublishBindingFailed: this.showPublishBindingFailed,
      showAddSaveTargetingModal: this.showAddSaveTargetingModal,
      legacyEstimateData: this.legacyEstimateData,
      spaceDeliverabilityData: this.spaceDeliverabilityData,
      creativeSummaryStepState: this.creativeSummaryStepState,
      deliveryTargetModalType: this.deliveryTargetModalType
    };
  }

  get noLegacyEstimateDes() {
    // if (this.flowModel.state.campaign.basic.priceModel === RtbCampaignPlanType.RB) {
    //   return i18n.t('campaignSummary.labels.rbNotEstimateDesc');
    // } else {
    //   return i18n.t('campaignSummary.labels.limitationNotEstimateDesc');
    // }
    return i18n.t('campaignSummary.labels.limitationNotEstimateDesc');
  }

  get showLegacyEstimation() {
    // TODO: adjust to Message channel
    return ChannelGroupSetting.RTB.key === this.flowModel.l1Object.channel;
  }

  get showSpaceDeliverability() {
    // RKB-2712：改用渠道判斷，完全不碰 adType。
    // PMP 活動的 adType / adTypeId 從後端來就是空的，`isAdTypeSupportOutdoorTA`
    // 對它恆為 false ⇒ 過去這塊板塊對 PMP 從來沒被 render 過。
    return this.channelGroupSetting.supportOutdoorTA();
  }

  get estimatedAudience() {
    return this.flowModel.estimatedAudience;
  }

  get audienceLowestThreshold() {
    return this.flowModel.audienceLowestThreshold;
  }

  get targetingValue() {
    return this.flowModel.limitationModel
      ? this.flowModel.limitationModel.limitationValue
      : [];
  }

  get order() {
    return this.flowModel.order;
  }

  get l1Object() {
    return this.flowModel.l1Object;
  }

  get campaign() {
    return this.flowModel.state.campaign!;
  }

  get savedTargetingList() {
    return this.flowModel.savedTAList.map((savedTA) => ({
      label: savedTA.name,
      value: savedTA.id
    }));
  }

  get appliedSavedTAInfo() {
    return this.flowModel.appliedSavedTAInfo;
  }

  getAdTypeSummaryData() {
    return {
      title: i18n.t('campaignSummary.titles.adType'),
      summaryData: {
        adType: {
          title: undefined,
          content: [
            {
              label: i18n.t('campaign.labels.adType'),
              value: this.getAdTypeValue(this.campaign.basic.adType)
            }
          ]
        }
      }
    };
  }

  get campaignSetupStepIndex() {
    const isCreate = this.flowModel.type === 'create';
    return isCreate
      ? CreateRtbCampaignSetupFlowStep.SETUP_CAMPAIGN
      : EditRtbCampaignSetupFlowStep.SETUP_CAMPAIGN;
  }

  getPmpErrors() {
    const { order, l1Object } = this.flowModel;

    let errors: { [key: string]: string | JSX.Element } = {};
    const { budget, startDate, endDate } = this.campaign.basic;
    if (budget > l1Object.budgetBalance) {
      errors.budget = renderOverBudgetWording(
        order.currency,
        l1Object.budgetBalance
      );
    } else {
      // POS-8115：預算不足時後端只回得出一句「錯誤的請求」（400000 被通用文案
      // 蓋掉），所以門檻在這裡講明白。PMP 流程跳過活動設定表單，走不到
      // RtbCampaignBasicFormValidator 那份同樣的檢查。
      const pmpFormModel = this.flowModel
        .campaignBasicFormModel as PmpFormModel;
      const minBudget = pmpFormModel.getMinBudgetOfPmpCampaign({
        startDate,
        endDate
      });
      if (budget < minBudget) {
        errors.budget = l1Object.autoOptimise
          ? i18n.t('campaign.descriptions.smallerThanDailyMnBudgetSum', {
              dmin: formatPriceWithCurrency(
                order.currency,
                order.campaignConstraint.budgetMinimum
              ),
              min: formatPriceWithCurrency(order.currency, minBudget)
            })
          : i18n.t('campaign.descriptions.smallerThanCampaignBudgetMinimum', {
              min: formatPriceWithCurrency(order.currency, minBudget)
            });
      }
    }

    if (
      moment(startDate).isBefore(moment(order.startDate)) ||
      moment(endDate).startOf('day').isAfter(moment(order.endDate))
    ) {
      errors.dayRange = i18n.t('campaign.descriptions.notWithinOrderDayRange');
    }
    return errors;
  }

  getPmpSummaryData() {
    const { state } = this.flowModel;
    const campaignBasic = state.campaign!.basic;
    const isPmpAdType = campaignBasic.adType === 'PMP';
    const canReviewPmpSummaryData =
      this.flowModel.isPmp || (this.isSummaryFlow && isPmpAdType);
    if (!canReviewPmpSummaryData) {
      return undefined;
    }
    const pmpFormModel = this.flowModel.campaignBasicFormModel as PmpFormModel;
    const { pmp, pmpSpaceOptions } = pmpFormModel;

    const errors = this.getPmpErrors();
    let pmpToGetSummary = pmp;
    if (!pmp) {
      const limitation = this.flowModel.state.campaign!.limitations;
      const adspaceLimitation = limitation.include.find(
        (item) => item.type === 'adspace'
      ) || {
        op: 'inc',
        type: 'adspace',
        value: []
      };
      const dealIdLimitation = limitation.other.find(
        (item) => item.type === 'dealId'
      ) || {
        op: 'inc',
        type: 'dealId',
        value: []
      };
      pmpToGetSummary = {
        name: campaignBasic.name,
        budget: campaignBasic.budget,
        startTime: campaignBasic.startDate,
        endTime: campaignBasic.endDate,
        dayPart: campaignBasic.dayPart,
        dealId: dealIdLimitation.value[0]?.value || '',
        spaces:
          (adspaceLimitation.value as SelectOptions[]).map(
            (v) => v.value as string
          ) || [],
        adAgencyIds: [this.flowModel.order.agencyId],
        channelGroupId: this.flowModel.l1Object.channelGroupId,
        pmpType: !_.isEmpty(campaignBasic.dayPart)
          ? PmpType.SPONSORSHIP
          : PmpType.BUYOUT
      } as Pmp;
    }

    return {
      title: i18n.t('campaignSummary.titles.basicSetting'),
      summaryData: {
        general: {
          title: i18n.t('campaignSummary.titles.generalInformation'),
          content: getPmpSummaryData(
            pmpToGetSummary,
            pmpSpaceOptions,
            this.flowModel.order.currency,
            this.isSummaryFlow ? undefined : errors
          )
        }
      },
      goStep: this.isSummaryFlow
        ? undefined
        : () => this.goStep(this.campaignSetupStepIndex, CampaignSetupTab.BASIC)
    };
  }

  getOptimizationSummary() {
    const campaignSetUpData = this.campaign.basic;
    const daypartSummary = campaignSetUpData.dayPart
      ? {
          label: i18n.t('campaignSummary.labels.dayPart'),
          value: this.getDayPartValue(campaignSetUpData.dayPart)
        }
      : undefined;
    const onlyDaypartingOptimize =
      this.flowModel.campaignBasicFormModel!.onlyDaypartingOptimize;
    if (onlyDaypartingOptimize) {
      return _.compact([daypartSummary]);
    }
    // RKB-2716: admin 覆寫過價格時，編輯頁的出價區塊會強制呈現為「出價上限」，
    // 這裡要跟著，否則同一個活動在編輯頁與資訊總覽會顯示不同的出價策略，
    // 而且出價上限那一列會整列消失。與編輯頁一樣只在顯示層強制，不動 formik 的值。
    const effectiveBidStrategy = campaignSetUpData.adminBidPrice
      ? BidStrategy.LOWEST_COST_WITH_BID_CAP
      : campaignSetUpData.bidStrategy;
    return _.compact([
      {
        label: i18n.t('adSetSetupFlow.mainStep.field.optimizationGoal'),
        value: i18n.t(
          `optimizationGoal.${campaignSetUpData.optimize.toLowerCase()}`
        )
      },
      {
        label: i18n.t('adSetSetupFlow.mainStep.field.bidStrategy'),
        value: i18n.t(
          `l1Object.labels.bidStrategy.${effectiveBidStrategy.toLowerCase()}`
        )
      },
      effectiveBidStrategy === BidStrategy.LOWEST_COST_WITH_BID_CAP
        ? {
            label: i18n.t('adSetSetupFlow.mainStep.field.bidControl'),
            // RKB-2716: 顯示 admin 調整過的價格，與活動列表、Dashboard、優化目標頁一致。
            value: formatPriceWithCurrency(
              this.flowModel.order.currency,
              +_.defaultTo(
                getDisplayPrice(
                  campaignSetUpData.adminBidPrice,
                  campaignSetUpData.bidPrice
                ),
                0
              )
            )
          }
        : undefined,
      {
        label: i18n.t('adSetSetupFlow.mainStep.field.billingEvent'),
        value:
          campaignSetUpData.priceModel === RtbCampaignPlanType.RS
            ? i18n.t(
                `rtbCampaign.billingEvent.${
                  campaignSetUpData.priceModel
                }.${campaignSetUpData.optimize.toLowerCase()}`
              )
            : i18n.t(`rtbCampaign.billingEvent.${campaignSetUpData.priceModel}`)
      },
      {
        label: i18n.t('campaignInfo.labels.creativeDeliverType'),
        value: this.getCreativeDeliverTypeValue(
          campaignSetUpData.creativeDeliverType
        )
      },
      campaignSetUpData.outdoorDeliverType && {
        label: i18n.t('campaignInfo.labels.outdoorDeliverType'),
        value: this.getOutdoorDeliverTypeValue(
          campaignSetUpData.outdoorDeliverType
        )
      },
      campaignSetUpData.frequency &&
        !_.isEmpty(campaignSetUpData.frequency) && {
          label: i18n.t('adSetSetupFlow.mainStep.field.frequencyControl'),
          value: this.getSessionCappaingCountValue(
            +campaignSetUpData.frequency.maxFrequency,
            +campaignSetUpData.frequency.intervalDays
          )
        },
      campaignSetUpData.dayPart && {
        label: i18n.t('campaignSummary.labels.dayPart'),
        value: this.getDayPartValue(campaignSetUpData.dayPart)
      }
    ]);
  }

  getSetUpSummaryData() {
    const campaignSetUpData = this.campaign.basic;
    const priceModelRelatedSummaryData = this.getPriceModelRelatedSummaryData(
      campaignSetUpData.priceModel
    );
    const optimizationSummary = this.getOptimizationSummary();
    return {
      title: i18n.t('campaignSummary.titles.basicSetting'),
      summaryData: _.omitBy(
        {
          general: {
            title: i18n.t('campaignSummary.titles.generalInformation'),
            content: _.compact(
              _.concat(
                [
                  {
                    label: i18n.t('campaignSummary.labels.orderName'),
                    value: this.flowModel.order.projectName
                  },
                  {
                    label: i18n.t('campaignSummary.labels.campaignName'),
                    value: campaignSetUpData.name
                  },
                  {
                    label: i18n.t('campaignSummary.labels.schedule'),
                    value: `${campaignSetUpData.startDate} ~ ${campaignSetUpData.endDate}`
                  },
                  {
                    label: i18n.t('campaignSummary.labels.pricingModel'),
                    value: this.getCampaignPlanTypeValue(
                      campaignSetUpData.priceModel
                    )
                  },
                  {
                    label: i18n.t('campaignSummary.labels.tags'),
                    value: this.getTags(campaignSetUpData.tags)
                  }
                  // {
                  //   label: i18n.t(
                  //     'campaignSummary.labels.enableMonitor'
                  //   ),
                  //   value: campaignSetUpData.enableMonitor
                  //     ? i18n.t('common.labels.yes')
                  //     : i18n.t('common.labels.no')
                  // }
                ],
                priceModelRelatedSummaryData
              )
            )
          },
          optimizeSetting:
            optimizationSummary.length > 0
              ? {
                  title: i18n.t(
                    'adSetSetupFlow.mainStep.fieldset.optimizationTitle'
                  ),
                  content: optimizationSummary
                }
              : undefined
        },
        _.isUndefined
      ),
      goStep: this.isSummaryFlow
        ? undefined
        : () => this.goStep(this.campaignSetupStepIndex, CampaignSetupTab.BASIC)
    } as FlowSummarySectionProps;
  }

  getLimitationsSummaryData(): FlowSummarySectionProps {
    const limitations = this.campaign.limitations;
    let summaryData = this.flowModel.getLimitationsSummaryData(limitations);
    const { adType, adTypeId, fixOutdoorSpace } = this.campaign.basic;
    const supportOutdoorTA = isAdTypeSupportOutdoorTA(adType, adTypeId);
    if (supportOutdoorTA) {
      summaryData = {
        fixOutdoorSpace: {
          title: i18n.t('limitation.title.fixOutdoorLimitationSection'),
          titlePrefixColor: SummaryTitleColor.DARK,
          content: [
            {
              label: i18n.t('campaignSummary.labels.fixOutdoorSpace'),
              value: fixOutdoorSpace
                ? i18n.t('common.labels.yes')
                : i18n.t('common.labels.no')
            }
          ]
        },
        ...summaryData
      };
    }
    return {
      title: i18n.t('campaignSummary.titles.targetSetting'),
      summaryData,
      goStep: this.isSummaryFlow
        ? undefined
        : () =>
            this.goStep(
              this.campaignSetupStepIndex,
              CampaignSetupTab.LIMITATION
            )
    };
  }

  getTrackingSummaryData() {
    const needShowConversionTracking =
      this.flowModel.addonFeatureManager.isFeatureEnable(
        ADDONFEATURE.CONVERSION_TRACKING.CONV_TRACKING_LIST
      );
    const needShowThirdPartyTracking =
      this.flowModel.addonFeatureManager.isFeatureEnable(
        ADDONFEATURE.CAMPAIGN.THIRDPARTY_TRACKING
      );
    const needShowReTargetingTracking =
      this.flowModel.addonFeatureManager.isFeatureEnable(
        ADDONFEATURE.CAMPAIGN.RETARGETING_TRACKER
      );
    if (
      !needShowConversionTracking &&
      !needShowThirdPartyTracking &&
      !needShowReTargetingTracking
    ) {
      return;
    }
    const campaignBasic = this.campaign.basic;
    const videoProgressTrackingCode = _.get(
      campaignBasic,
      'videoProgressTrackingCode.code'
    );
    const videoProgressTrackingOffset = _.get(
      campaignBasic,
      'videoProgressTrackingCode.offset'
    );
    const conversionTrackingContent = campaignBasic.conversionTracking && [
      {
        label: i18n.t('campaignSummary.labels.conversionTracking'),
        value: campaignBasic.conversionTracking.label
      }
    ];
    const conversionTrackingTypeOptions = _.get(
      this.flowModel.campaignBasicFormModel,
      'trackingConfig.conversionTracking.conversionTrackingTypeOptions',
      []
    );
    if (conversionTrackingTypeOptions.length > 0 && conversionTrackingContent) {
      conversionTrackingContent.push({
        label: i18n.t('campaignSummary.labels.convTrackEvent'),
        value:
          i18n.t(
            `campaignSummary.labels.convTrackEvent${_.startCase(
              campaignBasic.convTrackEvent
            )}`
          ) || i18n.t('campaignSummary.labels.convTrackEventClick')
      });
    }
    const summaryData: SummarySectionData = _.omitBy(
      {
        conversionTracking: needShowConversionTracking
          ? {
              title: i18n.t('campaignSummary.titles.conversionTracking'),
              content: conversionTrackingContent
            }
          : undefined,
        viewTrackingCodes: needShowThirdPartyTracking
          ? {
              title: i18n.t('campaignSummary.titles.trackingCodes'),
              content: campaignBasic.viewTrackingCodes &&
                campaignBasic.viewTrackingCodes.length > 0 && [
                  {
                    label: i18n.t('campaignSummary.labels.trackingCodes'),
                    value: campaignBasic.viewTrackingCodes
                      .map((code) => {
                        return code.trackingCode;
                      })
                      .join('\r\n')
                  }
                ]
            }
          : undefined,
        videoProgressTrackingCode: needShowReTargetingTracking
          ? {
              title: i18n.t('campaignSummary.titles.videoProgressTrackingCode'),
              content: videoProgressTrackingCode &&
                videoProgressTrackingOffset && [
                  {
                    label: i18n.t(
                      'campaignSummary.labels.videoProgressTrackingCode'
                    ),
                    value: videoProgressTrackingCode,
                    showArea: true
                  },
                  {
                    label: i18n.t(
                      'campaignSummary.labels.videoProgressTrackingOffset'
                    ),
                    value: `${videoProgressTrackingOffset} ${i18n.t(
                      'campaign.labels.sec'
                    )}`
                  }
                ]
            }
          : undefined
      },
      _.isUndefined
    ) as SummarySectionData;
    return {
      title: i18n.t('campaignSummary.titles.trackingSetting'),
      goStep: this.isSummaryFlow
        ? undefined
        : () =>
            this.goStep(this.campaignSetupStepIndex, CampaignSetupTab.TRACKING),
      summaryData
    };
  }

  getPriceModelRelatedSummaryData(priceModel) {
    const campaignSetUpData = this.campaign.basic;
    const needShowOrderPrice =
      priceModel !== RtbCampaignPlanType.RS &&
      priceModel !== RtbCampaignPlanType.DCPM;
    const needShowBudget = !_.get(
      this.flowModel.l1Object,
      'autoOptimise',
      false
    );
    const needShowDailyTargetBudget = true;
    const needShowVideoAdViewObjective =
      campaignSetUpData.videoAdViewObjective &&
      (([AdType.VIDEO, AdType.COMBO, AdType.UNKNOW].includes(
        campaignSetUpData.adType as AdType
      ) &&
        priceModel === RtbCampaignPlanType.FCPV) ||
        campaignSetUpData.adType === AdType.THIRD_PARTY_BOTTOM);
    return _.compact([
      needShowOrderPrice &&
        campaignSetUpData.orderPrice && {
          label: i18n.t('campaignSummary.labels.orderPrice', {
            model: this.getCampaignPlanTypeValue(priceModel)
          }),
          // RKB-2716: 顯示 admin 調整過的價格，與編輯頁一致。
          value: `${this.currencyLabel} ${getDisplayPrice(
            campaignSetUpData.adminBidPrice,
            campaignSetUpData.orderPrice
          )}`
        },
      needShowBudget && {
        label: i18n.t('campaignSummary.labels.campaignBudget'),
        value: `${this.currencyLabel} ${campaignSetUpData.budget}`
      },
      needShowDailyTargetBudget && {
        label: i18n.t('campaignSummary.labels.budgetDistribution'),
        value: this.getOptimizeBudgetValue(campaignSetUpData.dailyTargetBudget)
      },
      needShowVideoAdViewObjective && {
        label: i18n.t('campaignSummary.labels.videoAdViewObjective'),
        value: this.getVideoAdViewObjective(
          campaignSetUpData.videoAdViewObjective
        )
      }
    ]);
  }

  getCreativeBindingSummaryData() {
    const campaignBasic = this.campaign.basic;
    const bindings = campaignBasic.bindings;
    if (bindings && bindings.length > 0) {
      const now = moment();
      const isDeliverying =
        now.isAfter(moment(campaignBasic.startDate)) &&
        now.isBefore(moment(campaignBasic.endDate));
      const isActive = campaignBasic.state === CampaignState.ACTIVATE;
      const hasActiveBinding =
        bindings.find((binding) => binding.active) !== undefined;
      const showAlert = isDeliverying && isActive && hasActiveBinding;
      return {
        title: i18n.t('campaignSetupFlow.summaryStep.draftBindingTitle'),
        summaryData: {
          draftBinding: {
            content: _.compact([
              {
                label: i18n.t(
                  'campaignSetupFlow.summaryStep.labels.draftBinding'
                ),
                value: bindings.length,
                hintColor: SummaryTitleColor.RED,
                hint: showAlert
                  ? i18n.t(
                      'campaignSetupFlow.summaryStep.message.willDeliverImmediately'
                    )
                  : undefined
              }
            ])
          }
        }
      };
    }
    return;
  }

  getOptimizeBudgetValue(dailyTargetBudget) {
    if (
      dailyTargetBudget === null ||
      dailyTargetBudget === undefined ||
      dailyTargetBudget.toString() === '0'
    ) {
      return i18n.t('campaignSummary.labels.budgetScheduleDistribution');
    } else {
      return i18n.t('campaignSummary.labels.budgetDailyDistribution', {
        currency: this.currencyLabel,
        budget: dailyTargetBudget
      });
    }
  }

  getVideoAdViewObjective(videoAdViewObjective) {
    if (videoAdViewObjective.videoAdEvent === VideoAdViewObjective.METRIC) {
      return i18n.t('campaignSummary.labels.videoAdViewObjectiveMetric', {
        metric: i18n.t(
          `campaign.labels.${videoAdViewObjective.videoAdMetricEvent.toLowerCase()}`
        )
      });
    } else if (
      videoAdViewObjective.videoAdEvent === VideoAdViewObjective.PROGRESS
    ) {
      return i18n.t('campaignSummary.labels.videoAdViewObjectiveProgress', {
        offset: videoAdViewObjective.offset
      });
    } else {
      return i18n.t('campaignSummary.labels.videoAdViewObjectiveDefault');
    }
  }

  getDayPartValue(dayPart) {
    let dayPartValue = _.omitBy(_.omit(dayPart, 'enabled'), _.isEmpty);
    return Object.keys(dayPartValue)
      .map((day) => {
        return `${getDayOfWeekLabelByValue(parseInt(day, 10))},${i18n.t(
          'daypart.labels.hourUnit'
        )}: ${dayPartValue[day].join(', ')}`;
      })
      .join('\r\n');
  }

  getSessionCappaingCountValue(maxFrequency: number, intervalDays: number) {
    return i18n.t('adSetSetupFlow.mainStep.labels.frequencyControl', {
      event: i18n.t('adSetSetupFlow.mainStep.labels.impression'),
      eventPlural: +maxFrequency > 1 ? 's' : '',
      interval_days: intervalDays,
      max_frequency: maxFrequency,
      unit: i18n.t('common.units.day'),
      unitPlural: +intervalDays > 1 ? 's' : ''
    });
  }

  getCampaignPlanTypeValue(campaignPlanType) {
    return i18n.t(`campaign.labels.${campaignPlanType}`);
  }

  getAdTypeValue(adType) {
    const key = `campaignSummary.labels.adType${upperFirst(camelCase(adType))}`;
    if (!i18n.exists(key)) {
      return this.flowModel.adTypeSetting?.name || adType;
    }

    return i18n.t(key);
  }

  getDeliverTypeValue(deliverType) {
    switch (deliverType) {
      case DeliverType.ACCELERATED:
        return i18n.t('campaignSummary.labels.accelerated');
      default:
        return i18n.t('campaignSummary.labels.standard');
    }
  }

  getTags(tags) {
    if (!tags || tags.length === 0) {
      return i18n.t('summary.titles.noData');
    }

    return tags.join(',');
  }

  getCreativeDeliverTypeValue(creativeDeliverType) {
    switch (creativeDeliverType) {
      case CreativeDeliverType.OPTIMIZE:
        return i18n.t('campaignSummary.labels.creativeOptimizeDelivery');
      default:
        return i18n.t('campaignSummary.labels.creativeUniformDelivery');
    }
  }

  getOutdoorDeliverTypeValue(outdoorDeliverType: OutdoorDeliverType) {
    const key = OutdoorDeliverType[outdoorDeliverType];
    return i18n.t(`campaign.labels.outdoorDeliverType.${key.toLowerCase()}`);
  }

  async getLegacyEstimateData() {
    if (!this.showLegacyEstimation) {
      return;
    }
    const campaign = this.campaign;
    this.updateState(true);
    try {
      this.legacyEstimateData =
        await this.campaignManager.getLegacyEstimateData(
          campaign.basic,
          campaign.limitations,
          this.order.campaignBidPrice
        );
    } catch (e) {}
    this.updateState(false);
  }

  async getSpaceDeliverabilityData() {
    if (!this.showSpaceDeliverability) {
      return;
    }

    const { state, l1Object, campaignBasicFormModel } = this.flowModel;
    // 這道 gate 的語意是「這個活動的 limitation 是版位型的」。
    // RKB-2712：PMP 的 form model 不屬於 outdoor 那一系
    // （AbstractPmpFormModel extends DefaultRtbCampaignBasicFormModel），但它的
    // limitation 同樣是版位（SelectPmpStep 在 goNext 就寫入 type: 'adspace'），
    // 所以一併放行。其餘活動的行為不變（isPmpType 的 base 預設是 false）。
    if (
      !campaignBasicFormModel ||
      !(
        campaignBasicFormModel instanceof
          AbstractOutdoorLimitationCampaignFormModel ||
        campaignBasicFormModel.isPmpType
      )
    ) {
      return;
    }
    const limitations = this.campaign.limitations;
    this.updateState(true);
    try {
      this.spaceDeliverabilityData =
        await this.campaignManager.getSpaceDeliverabilityData(
          l1Object.channelGroupId,
          state.campaign!.basic.adType,
          // PMP 活動沒有 adTypeId，送 null（後端收到 null 時視為單螢幕、不進同播分支）。
          // 不從別處硬湊一個 —— 任挑一個都是任意選擇。
          state.campaign!.basic.adTypeId ?? null,
          isTestMode(limitations),
          limitations,
          // 編輯既有活動時要把自己從機台佔用數扣掉，不然會看到自己鎖著的機台被算成
          // 「超過鎖定上限」（ECOPD-5621）。新建活動還沒有 id，送 null。
          state.campaign!.basic.id ?? null
        );
    } catch (e) {}
    this.updateState(false);
  }

  submit = async () => {
    let campaignToSubmit = this.campaign;
    const formModel = this.flowModel.campaignBasicFormModel;
    if (formModel instanceof AbstractDynamicMovableOutdoorFormModel) {
      this.updateState(true);
      try {
        const { limitations, failureMessage } =
          await formModel.transformLimitationsForSubmit(
            campaignToSubmit.limitations
          );
        if (failureMessage) {
          toast.error(failureMessage);
          this.updateState(false);
          return;
        }
        campaignToSubmit = { ...campaignToSubmit, limitations };
      } catch (e) {
        toast.error(e instanceof Error ? e.message : String(e));
        this.updateState(false);
        return;
      }
    }
    if (this.flowModel.type === 'edit') {
      await this.updateCampaign(campaignToSubmit, this.flowModel.l1Object);
    } else if (this.flowModel.type === 'split') {
      await this.splitCampaign(
        campaignToSubmit,
        this.flowModel.campaignId,
        this.flowModel.l1Object
      );
    } else {
      await this.createCampaign(campaignToSubmit, this.flowModel.l1Object);
    }
  };

  splitCampaign = async (campaign: any, campaignId, l1Object: L1Object) => {
    this.updateState(true);
    try {
      const l1ObjectId = _.get(l1Object, 'l1ObjectId');
      await this.campaignManager.splitCampaign(
        campaign,
        campaignId,
        l1ObjectId
      );
      toast.success(i18n.t('common.messages.succeeded'));
      this.redirectToLastPage();
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  createCampaign = async (campaign: any, l1Object: L1Object) => {
    this.updateState(true);
    try {
      const l1ObjectId = _.get(l1Object, 'l1ObjectId');
      if (this.flowModel.isPmp && this.creativeSummaryStepModel) {
        const creative = await this.creativeSummaryStepModel.getJsonToSubmit();
        const pmpFormModel = this.flowModel
          .campaignBasicFormModel as PmpFormModel;
        const createCreative =
          this.flowModel.state.creativeSetupStepData?.props.model instanceof
          CreateCreativeInL2ObjectSetupFlowPageModel;
        if (createCreative) {
          await this.campaignManager.createPmpCampaignAndCreative(
            campaign,
            l1ObjectId,
            pmpFormModel.pmp!.id,
            creative
          );
        } else {
          await this.campaignManager.createPmpCampaignAndBindCreative(
            campaign,
            l1ObjectId,
            pmpFormModel.pmp!.id,
            [_.get(creative, 'id')!]
          );
        }
      } else if (this.creativeSummaryStepModel) {
        const creative = await this.creativeSummaryStepModel.getJsonToSubmit();
        const createCreative =
          this.flowModel.state.creativeSetupStepData?.props.model instanceof
          CreateCreativeInL2ObjectSetupFlowPageModel;
        if (createCreative) {
          await this.campaignManager.createCampaignAndCreative(
            campaign,
            l1ObjectId,
            creative
          );
        } else {
          await this.campaignManager.createCampaignAndBindCreative(
            campaign,
            l1ObjectId,
            [_.get(creative, 'id')!]
          );
        }
      } else {
        await this.campaignManager.createCampaign(campaign, l1ObjectId);
      }
      toast.success(i18n.t('common.messages.succeeded'));
      this.redirectToLastPage();
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  updateCampaign = async (campaign: any, l1Object: L1Object) => {
    this.updateState(true);
    try {
      const l1ObjectId = _.get(l1Object, 'l1ObjectId');
      await this.campaignManager.updateCampaign(campaign, l1ObjectId);
      toast.success(i18n.t('common.messages.succeeded'));
      this.redirectToLastPage();
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  redirectToLastPage = () => {
    const order = this.flowModel.order;
    const l1Object = this.flowModel.l1Object;
    const l1ObjectId = _.get(l1Object, 'l1ObjectId');
    const redirectPath = l1ObjectId
      ? `/orders/${order.orderNumber}/campaign-groups/${l1ObjectId}`
      : `/orders/${order.orderNumber}`;
    this.flowModel.setFinishedRedirectPath(redirectPath);
  };

  onShowAddSaveTargetingModal = (showModal: boolean) => {
    this.showAddSaveTargetingModal = showModal;
    this.updateState(false);
  };

  initCreativeSummaryModel(): void {
    if (!this.creativeSummaryStepModel) {
      return;
    }
    this.creativeSummaryDataHanlder = this.creativeSummaryStepModel.event.add(
      (model) => {
        this.creativeSummaryStepState = model.state;
        const { basicSummaryData, mediaSummaryData } =
          this.creativeSummaryStepState;
        if (basicSummaryData) {
          basicSummaryData.goStep = () =>
            this.goStep(
              CreateRtbCampaignWithCreativeSetupFlowStep.SETUP_CREATIVE
            );
        }
        if (mediaSummaryData) {
          mediaSummaryData.goStep = () =>
            this.goStep(
              CreateRtbCampaignWithCreativeSetupFlowStep.SETUP_CREATIVE
            );
        }
        this.event.fireEvent(this);
      }
    );
    this.creativeSummaryStepModel.initSummaryData();
  }

  openDeliveryTargetModal = (type: DeliveryTargetModalType) => {
    this.deliveryTargetModalType = type;
    this.updateState(false);
  };

  closeDeliveryTargetModal = () => {
    this.deliveryTargetModalType = undefined;
    this.updateState(false);
  };

  unmount(eventHandler?: number) {
    eventHandler && this.event.remove(eventHandler);

    if (!this.creativeSummaryStepModel || !this.creativeSummaryDataHanlder) {
      return;
    }
    this.creativeSummaryStepModel.event.remove(this.creativeSummaryDataHanlder);
  }

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}

export class RtbCampaignDraftSummaryStepModel extends DefaultRtbCampaignSummaryStepModel {
  constructor(
    private draftId: string,
    flowModel: RtbCampaignSetupFlowPageModel,
    subStepIndex: number,
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void,
    goSubStep: (subStepIndex: number) => void,
    private draftManager: DraftManager = new RtbCampaignDraftManager()
  ) {
    super(flowModel, subStepIndex, goLast, goStep, goSubStep);
  }

  submit = async () => {
    const campaign = this.campaign;
    this.updateState(true);
    try {
      const { includeBinding, result } = await this.draftManager.publishDraft(
        this.flowModel.l1Object.channelGroupId,
        this.draftId,
        this.campaignManager.prepareCreateCampaignPayload(campaign)
      );
      if (result === 'SUCCESS') {
        if (includeBinding) {
          toast.success(
            i18n.t('campaignSetupFlow.messages.publishL2L3Success')
          );
        } else {
          toast.success(i18n.t('campaignSetupFlow.messages.publishL2Success'));
        }
        this.redirectToLastPage();
      }
      if (result === 'PARTIAL_SUCCESS') {
        this.showPublishBindingFailed = true;
        this.updateState(false);
      } else if (result === 'FAIL') {
        toast.error(i18n.t('campaignSetupFlow.messages.publishL2L3Failed'));
        this.updateState(false);
      }
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };
}

export class MessageRtbCampaignSummaryStepModel extends DefaultRtbCampaignSummaryStepModel {
  getSetUpSummaryData() {
    const campaignSetUpData = this.campaign.basic;
    const priceModelRelatedSummaryData = this.getPriceModelRelatedSummaryData(
      campaignSetUpData.priceModel
    );
    return {
      title: i18n.t('campaignSummary.titles.basicSetting'),
      summaryData: _.omitBy(
        {
          general: {
            title: i18n.t('campaignSummary.titles.generalInformation'),
            content: _.compact(
              _.concat(
                [
                  {
                    label: i18n.t('campaignSummary.labels.orderName'),
                    value: this.flowModel.order.projectName
                  },
                  {
                    label: i18n.t('campaignSummary.labels.campaignName'),
                    value: campaignSetUpData.name
                  },
                  {
                    label: i18n.t('campaignSummary.labels.deliverDate'),
                    value: campaignSetUpData.startDate
                  },
                  {
                    label: i18n.t('campaignSummary.labels.messages'),
                    value: campaignSetUpData.messages
                  },
                  {
                    label: i18n.t('campaignSummary.labels.pricingModel'),
                    value: this.getCampaignPlanTypeValue(
                      campaignSetUpData.priceModel
                    )
                  },
                  {
                    label: i18n.t('campaignSummary.labels.tags'),
                    value: this.getTags(campaignSetUpData.tags)
                  }
                ],
                priceModelRelatedSummaryData
              )
            )
          },
          optimizeSetting: {
            title: i18n.t('adSetSetupFlow.mainStep.fieldset.optimizationTitle'),
            content: _.compact([
              {
                label: i18n.t('adSetSetupFlow.mainStep.field.optimizationGoal'),
                value: i18n.t(
                  `optimizationGoal.${campaignSetUpData.optimize.toLowerCase()}`
                )
              },
              {
                label: i18n.t('adSetSetupFlow.mainStep.field.billingEvent'),
                value: i18n.t(
                  `rtbCampaign.billingEvent.${campaignSetUpData.priceModel}`
                )
              }
            ])
          }
        },
        _.isUndefined
      ),
      goStep: this.isSummaryFlow
        ? undefined
        : () => this.goStep(this.campaignSetupStepIndex, CampaignSetupTab.BASIC)
    };
  }

  getPriceModelRelatedSummaryData(priceModel) {
    const campaignSetUpData = this.campaign.basic;
    const needShowOrderPrice =
      priceModel !== RtbCampaignPlanType.RS &&
      priceModel !== RtbCampaignPlanType.DCPM;
    const needShowBudget = !_.get(
      this.flowModel.l1Object,
      'autoOptimise',
      false
    );
    return _.compact([
      needShowOrderPrice &&
        campaignSetUpData.orderPrice && {
          label: i18n.t('campaignSummary.labels.orderPrice', {
            model: this.getCampaignPlanTypeValue(priceModel)
          }),
          // RKB-2716: 顯示 admin 調整過的價格，與編輯頁一致。
          value: `${this.currencyLabel} ${getDisplayPrice(
            campaignSetUpData.adminBidPrice,
            campaignSetUpData.orderPrice
          )}`
        },
      needShowBudget && {
        label: i18n.t('campaignSummary.labels.campaignBudget'),
        value: `${this.currencyLabel} ${campaignSetUpData.budget}`
      }
    ]);
  }
}

export class MessageRtbCampaignDraftSummaryStepModel extends MessageRtbCampaignSummaryStepModel {
  constructor(
    private draftId: string,
    flowModel: RtbCampaignSetupFlowPageModel,
    subStepIndex: number,
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void,
    goSubStep: (subStepIndex: number) => void,
    private draftManager: DraftManager = new RtbCampaignDraftManager()
  ) {
    super(flowModel, subStepIndex, goLast, goStep, goSubStep);
  }

  submit = async () => {
    const campaign = this.campaign;
    this.updateState(true);
    try {
      const { includeBinding, result } = await this.draftManager.publishDraft(
        this.flowModel.l1Object.channelGroupId,
        this.draftId,
        this.campaignManager.prepareCreateCampaignPayload(campaign)
      );
      if (result === 'SUCCESS') {
        if (includeBinding) {
          toast.success(
            i18n.t('campaignSetupFlow.messages.publishL2L3Success')
          );
        } else {
          toast.success(i18n.t('campaignSetupFlow.messages.publishL2Success'));
        }
        this.redirectToLastPage();
      }
      if (result === 'PARTIAL_SUCCESS') {
        this.showPublishBindingFailed = true;
        this.updateState(false);
      } else if (result === 'FAIL') {
        toast.error(i18n.t('campaignSetupFlow.messages.publishL2L3Failed'));
        this.updateState(false);
      }
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };
}
